import { useRef, useState } from 'react';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReportExportJob } from '@discovr/types';
import {
  type CreatedReportExport,
  cancelReportExport,
  isActiveReportExport,
  createReportExport,
  listReportExports,
  upsertReportExport,
} from '../api/reportExport';
import { campaignKeys } from '../queryKeys';
import { useReportExportAvailabilityStore } from '../state/useReportExportAvailabilityStore';
import { isReportExportUnavailable } from '../api/reportExportErrors';

const ACTIVE_EXPORT_POLL_MS = 5_000;

function shouldPoll(state: { status: string; data: ReportExportJob[] | undefined }): boolean {
  return state.status !== 'error' && Boolean(state.data?.some(isActiveReportExport));
}

/** SSE (to the requester and anyone who joined) is the primary signal. While an export is active the
 *  list also polls, in case an event is missed; it stops while the last fetch failed and when nothing
 *  is active. Opening the panel, remounting the button or refocusing the window refetches, so an admin
 *  who has not joined learns about a job from the next list fetch. */
export function useReportExports(campaignId: string) {
  return useQuery({
    queryKey: campaignKeys.reportExports(campaignId),
    queryFn: ({ signal }) => listReportExports(campaignId, signal),
    refetchInterval: (query) => (shouldPoll(query.state) ? ACTIVE_EXPORT_POLL_MS : false),
    enabled: Boolean(campaignId),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  });
}

function useInvalidateList(campaignId: string) {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: campaignKeys.reportExports(campaignId) });
}

function useCacheJob(campaignId: string) {
  const queryClient = useQueryClient();
  return (job: ReportExportJob) => {
    queryClient.setQueryData<ReportExportJob[]>(campaignKeys.reportExports(campaignId), (jobs) =>
      upsertReportExport(jobs, job),
    );
  };
}

/** Job ids with a request in flight. The ref answers a second click in the same tick; the state drives disabled rows. */
function useInFlightJobs() {
  const pending = useRef(new Set<string>());
  const [ids, setIds] = useState<ReadonlySet<string>>(new Set());
  const begin = (jobId: string) => {
    if (pending.current.has(jobId)) return false;
    pending.current.add(jobId);
    setIds(new Set(pending.current));
    return true;
  };
  const end = (jobId: string) => {
    pending.current.delete(jobId);
    setIds(new Set(pending.current));
  };
  return { ids, begin, end };
}

export interface UseCreateReportExportOptions {
  onSuccess: (created: CreatedReportExport) => void;
  onError: (err: Error) => void;
}

/** Starts an export, or joins the campaign's active one. The callbacks are mutation-level
 *  so they still run if the admin leaves the tab; the ref closes the gap before the first
 *  render disables the button, and the cache check covers a remounted button. */
export function useCreateReportExport(campaignId: string, { onSuccess, onError }: UseCreateReportExportOptions) {
  const queryClient = useQueryClient();
  const cacheJob = useCacheJob(campaignId);
  const invalidateList = useInvalidateList(campaignId);
  const mutationKey = campaignKeys.reportExport(campaignId);
  const isLocked = useRef(false);
  const markUnavailable = useReportExportAvailabilityStore((s) => s.markUnavailable);
  const clearUnavailable = useReportExportAvailabilityStore((s) => s.clearUnavailable);
  const mutation = useMutation<CreatedReportExport, Error, void>({
    mutationKey,
    mutationFn: () => createReportExport(campaignId),
    onSuccess: (created) => {
      clearUnavailable(campaignId);
      cacheJob(created.job);
      onSuccess(created);
    },
    onError: (err) => {
      if (isReportExportUnavailable(err)) markUnavailable(campaignId);
      onError(err);
    },
    onSettled: () => {
      isLocked.current = false;
      invalidateList();
    },
  });
  const isCreating = useIsMutating({ mutationKey }) > 0;
  const createExport = () => {
    if (isLocked.current || queryClient.isMutating({ mutationKey }) > 0) return;
    isLocked.current = true;
    mutation.mutate();
  };
  return { createExport, isCreating };
}

export function useCancelReportExport(campaignId: string, { onError }: { onError: (err: Error) => void }) {
  const cacheJob = useCacheJob(campaignId);
  const invalidateList = useInvalidateList(campaignId);
  const inFlight = useInFlightJobs();
  const mutation = useMutation<ReportExportJob, Error, string>({
    mutationFn: (jobId) => cancelReportExport(campaignId, jobId),
    onSuccess: cacheJob,
    onError: (err) => onError(err),
    onSettled: (_job, _err, jobId) => {
      inFlight.end(jobId);
      invalidateList();
    },
  });
  const cancelExport = (jobId: string) => {
    if (inFlight.begin(jobId)) mutation.mutate(jobId);
  };
  return { cancelExport, cancellingIds: inFlight.ids, isCancelling: inFlight.ids.size > 0 };
}
